// Watchlist sidebar tab (preview). Remove: delete this folder and the lines tagged "watchlist preview" in ui/app.ts.

import './watchlist.css';
import { escHtml as esc, toast } from '../ui/toast';
import { load, save, seed, splitPath, statusOf, uid, type Item, type Leg, type Status, type Venue } from './data';
import { cleanTicker, lookup, type KalshiGetMarket } from './lookup';
import { renderFolders, renderList, type ViewState } from './views';

type Layout = 'folders' | 'list';
type Filter = Status | 'all';

const LAYOUT_KEY = 'kg_watchlist_preview_layout';
/** Open contracts are re-read from the venues this often, so settled ones move across by themselves. */
const REFRESH_MS = 5 * 60_000;
/** First refresh waits for the extension to come up; Kalshi lookups fail until it has. */
const FIRST_REFRESH_MS = 15_000;

interface Draft {
  kalshi: { raw: string; leg: Leg | null; err: string; busy: boolean };
  polymarket: { raw: string; leg: Leg | null; err: string; busy: boolean };
  nameTouched: boolean;
}

class WatchlistPreview {
  private items: Item[] = load();
  private layout: Layout = 'folders';
  private filter: Filter = 'open';
  private q = '';
  private expanded = new Set<string>();
  private openId: string | null = null;
  private draft: Draft | null = null;

  constructor(private el: HTMLElement, private kalshi: KalshiGetMarket) {
    try {
      const v = localStorage.getItem(LAYOUT_KEY);
      if (v === 'folders' || v === 'list') this.layout = v;
    } catch { /* storage blocked */ }
    this.expandOpen();
  }

  mount(): void {
    this.el.innerHTML = `
      <div class="wl-top">
        <div class="wl-seg" id="wlLayouts"><button data-layout="folders">Folders</button><button data-layout="list">List</button></div>
        <div class="wl-seg" id="wlFilter"><button data-f="open">Open</button><button data-f="settled">Settled</button><button data-f="all">All</button></div>
      </div>
      <input class="set-in wl-search" id="wlSearch" type="search" placeholder="Search name, ticker, folder…"/>
      <div class="pnl-divider"></div>
      <div class="pnl-hdr-row">
        <span class="pnl-hdr" style="margin-bottom:0">Saved <span class="wl-count" id="wlTotal"></span></span>
        <span class="wl-acts"><span class="pnl-toggle-btn" id="wlAddBtn">add</span><span class="pnl-toggle-btn" id="wlReset" title="Replace everything with the demo contracts">reset</span></span>
      </div>
      <div id="wlForm"></div>
      <div id="wlList"></div>
      <datalist id="wlFolders"></datalist>`;
    this.wire();
    this.render();
    window.setTimeout(() => void this.refresh(), FIRST_REFRESH_MS);
    window.setInterval(() => void this.refresh(), REFRESH_MS);
  }

  private $<T extends HTMLElement = HTMLElement>(id: string): T {
    return this.el.querySelector('#' + id) as T;
  }

  private expandOpen(): void {
    for (const it of this.items) {
      if (statusOf(it) !== 'open') continue;
      for (let i = 1; i <= it.path.length; i++) this.expanded.add(it.path.slice(0, i).join('/'));
    }
  }

  private wire(): void {
    this.$('wlLayouts').addEventListener('click', (e) => {
      const l = (e.target as HTMLElement).closest('button')?.dataset.layout as Layout | undefined;
      if (!l) return;
      this.layout = l;
      try { localStorage.setItem(LAYOUT_KEY, l); } catch { /* storage blocked */ }
      this.render();
    });
    this.$('wlFilter').addEventListener('click', (e) => {
      const f = (e.target as HTMLElement).closest('button')?.dataset.f as Filter | undefined;
      if (f) { this.filter = f; this.render(); }
    });
    this.$('wlSearch').addEventListener('input', (e) => {
      this.q = (e.target as HTMLInputElement).value.trim().toLowerCase();
      this.renderItems();
    });
    this.$('wlAddBtn').addEventListener('click', () => {
      this.draft = this.draft ? null : {
        kalshi: { raw: '', leg: null, err: '', busy: false },
        polymarket: { raw: '', leg: null, err: '', busy: false },
        nameTouched: false,
      };
      this.renderForm();
    });
    this.$('wlReset').addEventListener('click', () => {
      if (!confirm('Replace every saved contract with the demo contracts?')) return;
      this.items = seed();
      this.openId = null;
      this.expandOpen();
      this.commit('Demo contracts restored');
      void this.refresh();
    });
    const list = this.$('wlList');
    list.addEventListener('click', (e) => this.onAction(e));
    list.addEventListener('change', (e) => this.onEdit(e.target as HTMLElement));
  }

  private onAction(e: Event): void {
    const el = (e.target as HTMLElement).closest<HTMLElement>('[data-act]');
    if (!el) return;
    const { act, id, key } = el.dataset;
    if (act === 'toggle' && key !== undefined) {
      if (this.expanded.has(key)) this.expanded.delete(key);
      else this.expanded.add(key);
    } else if (act === 'open' && id) {
      this.openId = this.openId === id ? null : id;
    } else if (act === 'del') {
      const it = this.items.find((x) => x.id === id);
      if (!it) return;
      this.items = this.items.filter((x) => x.id !== id);
      this.openId = null;
      this.commit(`Deleted “${it.name}”`);
      return;
    } else {
      return;
    }
    this.renderItems();
  }

  private onEdit(el: HTMLElement): void {
    const it = this.items.find((x) => x.id === el.dataset.id);
    if (!it) return;
    const v = (el as HTMLInputElement).value.trim();
    if (el.dataset.edit === 'name') {
      if (!v) { toast('A name is required'); this.renderItems(); return; }
      it.name = v;
      this.commit();
    } else if (el.dataset.edit === 'path') {
      it.path = splitPath(v);
      this.reveal(it);
      this.commit('Moved');
    }
  }

  private commit(msg?: string): void {
    save(this.items);
    this.render();
    if (msg) toast(msg, 'ok');
  }

  private visible(): Item[] {
    return this.items.filter((it) => {
      if (this.filter !== 'all' && statusOf(it) !== this.filter) return false;
      if (!this.q) return true;
      return [it.name, it.path.join('/'), ...it.legs.flatMap((l) => [l.ticker, l.title])].join(' ').toLowerCase().includes(this.q);
    });
  }

  private render(): void {
    this.$('wlLayouts').querySelectorAll('button').forEach((b) => b.classList.toggle('on', b.dataset.layout === this.layout));
    this.$('wlFilter').querySelectorAll('button').forEach((b) => b.classList.toggle('on', b.dataset.f === this.filter));
    const folders = new Set<string>();
    for (const it of this.items) for (let i = 1; i <= it.path.length; i++) folders.add(it.path.slice(0, i).join(' / '));
    this.$('wlFolders').innerHTML = [...folders].sort().map((f) => `<option value="${esc(f)}">`).join('');
    this.renderItems();
  }

  private renderItems(): void {
    const items = this.visible();
    this.$('wlTotal').textContent = String(items.length);
    const s: ViewState = { items, expanded: this.expanded, openId: this.openId };
    this.$('wlList').innerHTML = this.layout === 'folders' ? renderFolders(s) : renderList(s);
  }

  /** Re-read every open contract; failures (e.g. Kalshi before the extension is up) keep the saved values. */
  private async refresh(): Promise<void> {
    let changed = false;
    await Promise.all(this.items.filter((it) => statusOf(it) === 'open').flatMap((it) => it.legs.map(async (l, i) => {
      try {
        const fresh = await lookup(l.venue, l.ticker, this.kalshi);
        if (fresh.status !== l.status || fresh.expiresAt !== l.expiresAt) {
          it.legs[i] = { ...l, status: fresh.status, expiresAt: fresh.expiresAt };
          changed = true;
        }
      } catch { /* try again next cycle */ }
    })));
    if (changed) { save(this.items); this.renderItems(); }
  }

  // ---------- add

  private renderForm(): void {
    this.$('wlAddBtn').classList.toggle('on', !!this.draft);
    const host = this.$('wlForm');
    if (!this.draft) { host.innerHTML = ''; return; }
    host.innerHTML = `<form class="wl-form" id="wlAdd" autocomplete="off">
      <label class="wl-f">Kalshi ticker<input class="set-in mono" name="kalshi" placeholder="KXNFLGAME-26SEP27KCMIA-KC"/></label>
      <div class="wl-look" data-look="kalshi"></div>
      <label class="wl-f">Polymarket slug<input class="set-in mono" name="polymarket" placeholder="aec-nfl-kc-mia-2026-09-27"/></label>
      <div class="wl-look" data-look="polymarket"></div>
      <label class="wl-f">Name<input class="set-in" name="name" placeholder="Filled from the first market"/></label>
      <label class="wl-f">Folder<input class="set-in" name="path" list="wlFolders" placeholder="NFL / 2026 Wk4 / KC @ MIA  (optional)"/></label>
      <div class="set-note">One market saves it on its own; both save an arb pair. Paste a ticker, a slug, or a venue link.</div>
      <div class="wl-err" id="wlErr"></div>
      <div class="wl-form-btns"><button type="button" class="sb-foot-btn" data-cancel>Cancel</button><button class="sb-foot-btn wl-primary">Save</button></div>
    </form>`;
    const form = host.querySelector('form')!;
    const nameIn = form.elements.namedItem('name') as HTMLInputElement;
    nameIn.addEventListener('input', () => { this.draft!.nameTouched = nameIn.value.trim() !== ''; });
    for (const venue of ['kalshi', 'polymarket'] as Venue[]) {
      const input = form.elements.namedItem(venue) as HTMLInputElement;
      let timer = 0;
      input.addEventListener('input', () => {
        window.clearTimeout(timer);
        timer = window.setTimeout(() => void this.resolve(venue, input.value, form), 400);
      });
    }
    form.addEventListener('submit', (e) => { e.preventDefault(); this.submit(form); });
    form.querySelector('[data-cancel]')!.addEventListener('click', () => { this.draft = null; this.renderForm(); });
    (form.elements.namedItem('kalshi') as HTMLInputElement).focus();
  }

  private async resolve(venue: Venue, raw: string, form: HTMLFormElement): Promise<void> {
    const d = this.draft;
    if (!d) return;
    const slot = d[venue];
    const ticker = cleanTicker(venue, raw);
    slot.raw = ticker;
    slot.leg = null;
    slot.err = '';
    slot.busy = !!ticker;
    this.paintLook(venue, form);
    if (!ticker) { this.autoName(form); return; }
    try {
      const leg = await lookup(venue, ticker, this.kalshi);
      if (slot.raw !== ticker) return;
      slot.leg = leg;
    } catch (e) {
      if (slot.raw !== ticker) return;
      const msg = (e as Error).message || 'lookup failed';
      slot.err = venue === 'kalshi' && !/not found/i.test(msg) ? `${msg} — Kalshi lookups need the extension connected` : msg;
    }
    slot.busy = false;
    this.paintLook(venue, form);
    this.autoName(form);
  }

  private paintLook(venue: Venue, form: HTMLFormElement): void {
    const slot = this.draft![venue];
    const el = form.querySelector<HTMLElement>(`[data-look="${venue}"]`)!;
    el.className = 'wl-look' + (slot.err ? ' bad' : slot.leg ? ' ok' : '');
    el.textContent = slot.busy ? 'Looking up…'
      : slot.err ? slot.err
      : slot.leg ? `${slot.leg.title}${slot.leg.expiresAt ? ` · ends ${new Date(slot.leg.expiresAt).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}` : ''}${slot.leg.status === 'settled' ? ' · settled' : ''}`
      : '';
  }

  private autoName(form: HTMLFormElement): void {
    const d = this.draft!;
    if (d.nameTouched) return;
    const first = d.kalshi.leg ?? d.polymarket.leg;
    (form.elements.namedItem('name') as HTMLInputElement).value = first?.title ?? '';
  }

  private submit(form: HTMLFormElement): void {
    const d = this.draft!;
    const err = form.querySelector('#wlErr')!;
    const slots = [d.kalshi, d.polymarket].filter((s) => s.raw);
    if (!slots.length) { err.textContent = 'Paste at least one ticker.'; return; }
    if (slots.some((s) => s.busy)) { err.textContent = 'Still looking up — try again in a moment.'; return; }
    if (slots.some((s) => !s.leg)) { err.textContent = 'Fix the market that could not be found first.'; return; }
    const legs = slots.map((s) => s.leg!);
    const name = (form.elements.namedItem('name') as HTMLInputElement).value.trim() || legs[0].title;
    const it: Item = { id: uid(), path: splitPath((form.elements.namedItem('path') as HTMLInputElement).value), name, legs, addedAt: Date.now() };
    this.items.push(it);
    this.reveal(it);
    this.openId = it.id;
    this.draft = null;
    this.renderForm();
    this.commit(`Saved “${it.name}”`);
  }

  /** Open the item's folders and widen the filter so a just-saved or moved item is on screen. */
  private reveal(it: Item): void {
    for (let i = 1; i <= it.path.length; i++) this.expanded.add(it.path.slice(0, i).join('/'));
    if (!it.path.length) this.expanded.add('Unfiled');
    if (this.filter !== 'all' && this.filter !== statusOf(it)) this.filter = 'all';
  }
}

export function mountWatchlistPreview(el: HTMLElement, kalshiGetMarket: KalshiGetMarket): void {
  new WatchlistPreview(el, kalshiGetMarket).mount();
}
